import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, SmokeAlarm } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

const TYPE_LABEL: Record<string, string> = {
  smoke_only:    'Smoke only',
  co_combo:      'Smoke + CO',
  heat_detector: 'Heat detector',
  smart:         'Smart alarm',
};

const POWER_LABEL: Record<string, string> = {
  battery:           'Battery',
  hardwired:         'Hardwired',
  hardwired_backup:  'Hardwired + backup battery',
};

function alarmAge(alarm: SmokeAlarm): number | null {
  if (!alarm.install_year) return null;
  return CURRENT_YEAR - alarm.install_year;
}

function ageBadge(age: number | null): { label: string; color: string; bg: string } | null {
  if (age === null) return null;
  if (age >= 10) return { label: `${age}y — EXPIRED`, color: '#b91c1c', bg: '#fef2f2' };
  if (age >= 8)  return { label: `${age}y — Replace soon`, color: '#c2410c', bg: '#fff7ed' };
  return { label: `${age}y old`, color: '#374151', bg: '#f9fafb' };
}

function lastTestedWarning(lastTested: string | null): boolean {
  if (!lastTested) return true;
  const d = new Date(lastTested);
  const diff = (Date.now() - d.getTime()) / 86400000;
  return diff > 30;
}

export function SmokeAlarms() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const home = (homesData?.data ?? []).find(h => h.is_primary) ?? homesData?.data?.[0];

  const { data: alarmsData, isLoading } = useQuery({
    queryKey: ['smoke-alarms', home?.id],
    queryFn: () => api.get<{ data: SmokeAlarm[] }>(`/homes/${home!.id}/smoke-alarms`),
    enabled: !!home,
  });
  const alarms = alarmsData?.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/smoke-alarms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smoke-alarms', home?.id] });
      setConfirmDelete(null);
    },
  });

  const expiredCount  = alarms.filter(a => (alarmAge(a) ?? 0) >= 10).length;
  const notTestedCount = alarms.filter(a => lastTestedWarning(a.last_tested)).length;

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 100 }}>
      <div style={{ background: '#1D4ED8', padding: '48px 20px 24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}
        >
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff' }}>Smoke Alarms</h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>
              {alarms.length} alarm{alarms.length !== 1 ? 's' : ''} tracked
            </p>
          </div>
          <button
            onClick={() => navigate('/app/smoke-alarms/add')}
            style={{
              background: '#fff', color: '#1D4ED8', border: 'none',
              borderRadius: 10, padding: '10px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            + Add alarm
          </button>
        </div>
      </div>

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Safety status banner */}
        {(expiredCount > 0 || notTestedCount > 0) && (
          <div style={{
            padding: '14px 16px', background: expiredCount > 0 ? '#fef2f2' : '#fffbeb',
            borderRadius: 12, border: `1.5px solid ${expiredCount > 0 ? '#fecaca' : '#fde68a'}`,
          }}>
            {expiredCount > 0 && (
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#b91c1c' }}>
                {expiredCount} alarm{expiredCount > 1 ? 's' : ''} expired — replace immediately
              </p>
            )}
            {notTestedCount > 0 && (
              <p style={{ margin: expiredCount > 0 ? '4px 0 0' : 0, fontWeight: 500, fontSize: 14, color: '#92400e' }}>
                {notTestedCount} alarm{notTestedCount > 1 ? 's' : ''} not tested in 30 days
              </p>
            )}
          </div>
        )}

        {/* Recommended locations guide */}
        {alarms.length === 0 && !isLoading && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}>
            <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 15, color: '#111827' }}>
              Recommended alarm locations
            </p>
            {[
              'Every bedroom',
              'Outside each sleeping area / hallway',
              'Kitchen (not directly above stove)',
              'Basement (ceiling at base of stairs)',
              'Living room and main floor',
              'Garage (if attached)',
            ].map(loc => (
              <p key={loc} style={{ margin: '4px 0', fontSize: 14, color: '#374151' }}>
                • {loc}
              </p>
            ))}
          </div>
        )}

        {isLoading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>
        )}

        {alarms.map(alarm => {
          const age  = alarmAge(alarm);
          const badge = ageBadge(age);
          const needsTest = lastTestedWarning(alarm.last_tested);

          return (
            <div
              key={alarm.id}
              style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1.5px solid #e5e7eb' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 16, color: '#111827' }}>
                    {alarm.location}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                    {alarm.alarm_type ? TYPE_LABEL[alarm.alarm_type] : 'Unknown type'}
                    {alarm.power_type ? ` · ${POWER_LABEL[alarm.power_type]}` : ''}
                    {alarm.brand ? ` · ${alarm.brand}` : ''}
                  </p>
                  {alarm.install_year && (
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: '#9ca3af' }}>
                      Installed {alarm.install_year}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  {badge && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                      background: badge.bg, color: badge.color,
                    }}>
                      {badge.label}
                    </span>
                  )}
                  {needsTest && (
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                      background: '#fffbeb', color: '#92400e',
                    }}>
                      Test overdue
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {alarm.last_tested && (
                  <span style={{ fontSize: 12, color: '#6b7280', background: '#f9fafb', padding: '4px 8px', borderRadius: 6 }}>
                    Last tested: {alarm.last_tested}
                  </span>
                )}
                {alarm.last_battery && (
                  <span style={{ fontSize: 12, color: '#6b7280', background: '#f9fafb', padding: '4px 8px', borderRadius: 6 }}>
                    Battery: {alarm.last_battery}
                  </span>
                )}
              </div>

              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setConfirmDelete(alarm.id)}
                  style={{
                    flex: 1, padding: '8px', background: '#fef2f2', color: '#ef4444',
                    border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500,
                  }}
                >
                  Remove
                </button>
              </div>

              {confirmDelete === alarm.id && (
                <div style={{ marginTop: 10, padding: '12px', background: '#fef2f2', borderRadius: 8 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>
                    Remove {alarm.location} smoke alarm?
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => deleteMutation.mutate(alarm.id)}
                      disabled={deleteMutation.isPending}
                      style={{
                        flex: 1, padding: '8px', background: '#ef4444', color: '#fff',
                        border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600,
                      }}
                    >
                      {deleteMutation.isPending ? 'Removing…' : 'Yes, remove'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      style={{
                        flex: 1, padding: '8px', background: '#fff', color: '#374151',
                        border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!isLoading && alarms.length === 0 && (
          <button
            onClick={() => navigate('/app/smoke-alarms/add')}
            style={{
              padding: '16px', background: '#eff6ff', color: '#1D4ED8',
              border: '2px dashed #bfdbfe', borderRadius: 12,
              fontWeight: 600, fontSize: 15, cursor: 'pointer', width: '100%',
            }}
          >
            + Add your first smoke alarm
          </button>
        )}
      </div>

      <NavBar />
    </div>
  );
}
